import { useAuth } from '@/contexts/AuthContext'
import Config from './Config.js'
import { hasAdminPrivileges } from './adminUtils.js'
import SubscriptionService from './services/SubscriptionService'

// Check if user has an active subscription (paid plan or trial)
export const hasActiveSubscription = (currentUserData) => {
  // Admin users bypass all restrictions
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }

  // Check if user is in trial using SubscriptionService
  const isInTrial = !SubscriptionService.hasUsedTrial(currentUserData) && 
                   currentUserData?.trialData && 
                   currentUserData.trialData.endDate > new Date().getTime() && 
                   !currentUserData.trialData.expired

  // Check if user has paid subscription (including canceled but not expired)
  let hasPaidSubscription = false
  if (currentUserData?.subscriptions && currentUserData.subscriptions.length > 0) {
    // Check if any subscription is still valid (active or canceled but not expired)
    hasPaidSubscription = currentUserData.subscriptions.some(subscription =>
      SubscriptionService.isSubscriptionValid(subscription)
    )
  }

  return isInTrial || hasPaidSubscription
}

// Check if user can add devices
export const canAddDevices = (currentUserData, currentDeviceCount, userSubscriptions = [], subscriptions = []) => {
  // Admin users can add unlimited devices
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }

  // Get device limit using SubscriptionService
  const deviceLimit = getDeviceLimit(currentUserData, userSubscriptions, subscriptions)

  return currentDeviceCount < deviceLimit
}

// Check if user can add schedules
export const canAddSchedules = (currentUserData) => {
  // Admin users can add unlimited schedules
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }
  
  return hasActiveSubscription(currentUserData)
}

// Check if user can create screens (limit based on subscription)
export const canCreateScreens = (currentUserData, currentScreenCount, userSubscriptions = [], subscriptions = []) => {
  // Admin users can create unlimited screens
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }
  
  // Get screen limit using SubscriptionService
  const screenLimit = getScreenLimit(currentUserData, userSubscriptions, subscriptions)
  
  return currentScreenCount < screenLimit
}

// Check if user can create video clips (requires active subscription)
export const canCreateVideoClips = (currentUserData) => {
  // Admin users can create video clips
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }
  
  // Only users with active subscriptions (trial or paid) can create video clips
  if (!hasActiveSubscription(currentUserData)) {
    return false
  }
  
  return true
}

// Count devices currently running screens (not channels)
export const countDevicesRunningScreens = (devices) => {
  if (!devices || !Array.isArray(devices)) {
    return 0
  }
  
  return devices.filter(device => 
    device.screenId && device.screenId !== '' && 
    (!device.channelId || device.channelId === '')
  ).length
}

// Check if user can assign screen to device (considering device limits for screens only)
export const canAssignScreenToDevice = (currentUserData, devices, userSubscriptions = [], subscriptions = [], excludeDeviceId = null) => {
  // Admin users can assign screens to unlimited devices
  if (hasAdminPrivileges(currentUserData)) {
    return { canAssign: true, reason: null }
  }

  // Count devices currently running screens, excluding the device being assigned to
  const devicesRunningScreens = devices.filter(device => 
    device.screenId && device.screenId !== '' && 
    (!device.channelId || device.channelId === '') &&
    device.key !== excludeDeviceId
  ).length
  
  // Get device limit for screens
  const deviceLimit = getDeviceLimit(currentUserData, userSubscriptions, subscriptions)
  
  if (devicesRunningScreens >= deviceLimit) {
    return { 
      canAssign: false, 
      reason: `You have reached your device limit for screens (${deviceLimit}). Live channels are unlimited and can be assigned to any number of devices.` 
    }
  }
  
  return { canAssign: true, reason: null }
}

// Get subscription status message
export const getSubscriptionMessage = (currentUserData) => {
  // Admin users don't need subscription messages
  if (hasAdminPrivileges(currentUserData)) {
    return null
  }
  
  if (hasActiveSubscription(currentUserData)) {
    return null // No message needed for subscribers
  }
  
  return "This feature requires an active subscription. Please upgrade to continue."
}

// Get device limit for user (admin users get unlimited)
export const getDeviceLimit = (currentUserData, userSubscriptions = [], subscriptions = []) => {
  if (hasAdminPrivileges(currentUserData)) {
    return Infinity // Unlimited for admin users
  }

  // Check for device override first
  if (currentUserData?.deviceOverride && currentUserData.deviceOverride > 0) {
    return currentUserData.deviceOverride;
  }

  // Use SubscriptionService to get device limit
  return SubscriptionService.getDeviceLimit(currentUserData, userSubscriptions, subscriptions)
}

// Get screen limit for user (admin users get unlimited)
export const getScreenLimit = (currentUserData, userSubscriptions = [], subscriptions = []) => {
  if (hasAdminPrivileges(currentUserData)) {
    return Infinity // Unlimited for admin users
  }

  // Use SubscriptionService to get screen limit
  return SubscriptionService.getScreenLimit(currentUserData, userSubscriptions, subscriptions)
}

// Check if user is on free plan (no admin privileges, no trial, no paid subscription)
export const isFreePlanUser = (currentUserData) => {
  // Admin, test, and demo users are not free plan users
  if (hasAdminPrivileges(currentUserData)) {
    return false
  }
  
  // Users with active subscription (trial or paid) are not free plan users
  if (hasActiveSubscription(currentUserData)) {
    return false
  }
  
  // If user has no admin privileges and no active subscription, they are on free plan
  return true
}

// Check if user can use AI features (admin users always can)
export const canUseAIFeatures = (currentUserData) => {
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }
  
  // Only users with active subscriptions (trial or paid) can use AI features
  // Free users cannot use AI features
  return hasActiveSubscription(currentUserData)
}

// Check if user can use advanced features (admin users always can)
export const canUseAdvancedFeatures = (currentUserData) => {
  if (hasAdminPrivileges(currentUserData)) {
    return true
  }
  
  return hasActiveSubscription(currentUserData)
}

 